import type { TileTraffic } from '../gen/traffic/network';

/**
 * Pedestrians, pure (no three.js), in render space. They walk the sidewalk paths (TileTraffic.walks)
 * at their own pace, a little to one side of the path. On open paths (cut by buildings) they turn
 * back at the ends; now and then they stop for a few seconds or turn around.
 */
export interface WalkPath {
  pts: Float32Array;
  cum: Float32Array;
  length: number;
  closed: boolean;
  tile: string;
}

export interface Ped {
  path: WalkPath;
  s: number;
  dir: 1 | -1;
  speed: number;
  /** Sideways offset from the path (m), a per-person colour seed, the stride phase. */
  side: number;
  look: number;
  phase: number;
  pause: number;
  /** Seconds kept waiting by an obstacle ahead (turns back after a while). */
  waited?: number;
  /** Seconds left running from a car bearing down on them. */
  panic?: number;
  dead: boolean;
  prev: [number, number, number, number];
  cur: [number, number, number, number];
}

export class PedNet {
  readonly paths: WalkPath[] = [];
  private readonly byTile = new Map<string, WalkPath[]>();

  addTile(tile: string, data: TileTraffic): void {
    if (this.byTile.has(tile)) return;
    const list: WalkPath[] = [];
    for (const w of data.walks) {
      const pts = Float32Array.from(w.closed ? [...w.pts, w.pts[0], w.pts[1], w.pts[2]] : w.pts);
      const n = pts.length / 3;
      if (n < 2) continue;
      const cum = new Float32Array(n);
      for (let i = 1; i < n; i++) cum[i] = cum[i - 1] + Math.hypot(pts[i * 3] - pts[i * 3 - 3], pts[i * 3 + 2] - pts[i * 3 - 1]);
      if (cum[n - 1] < 8) continue;
      const p: WalkPath = { pts, cum, length: cum[n - 1], closed: w.closed, tile };
      list.push(p);
      this.paths.push(p);
    }
    this.byTile.set(tile, list);
  }

  removeTile(tile: string): void {
    const list = this.byTile.get(tile);
    if (!list) return;
    for (const p of list) {
      const i = this.paths.indexOf(p);
      if (i >= 0) this.paths.splice(i, 1);
    }
    this.byTile.delete(tile);
  }

  hasTile(tile: string): boolean {
    return this.byTile.has(tile);
  }

  get tiles(): IterableIterator<string> {
    return this.byTile.keys();
  }
}

export function samplePath(p: WalkPath, s: number, side: number, out: [number, number, number, number]): [number, number, number, number] {
  const { pts, cum } = p;
  const n = cum.length;
  s = Math.max(0, Math.min(p.length, s));
  let i = 1;
  while (i < n - 1 && cum[i] < s) i++;
  const t = (s - cum[i - 1]) / Math.max(1e-6, cum[i] - cum[i - 1]);
  const a = (i - 1) * 3;
  const b = i * 3;
  const dx = pts[b] - pts[a];
  const dz = pts[b + 2] - pts[a + 2];
  const l = Math.hypot(dx, dz) || 1;
  out[0] = pts[a] + dx * t - (dz / l) * side;
  out[1] = pts[a + 1] + (pts[b + 1] - pts[a + 1]) * t;
  out[2] = pts[a + 2] + dz * t + (dx / l) * side;
  out[3] = Math.atan2(-dz, dx);
  return out;
}

export class PedSim {
  readonly peds: Ped[] = [];
  private rand: () => number;

  constructor(
    readonly net: PedNet,
    seed = 7,
  ) {
    let s = seed >>> 0 || 1;
    this.rand = () => ((s = (Math.imul(s, 1664525) + 1013904223) >>> 0) / 4294967296);
  }

  spawn(path: WalkPath, s: number): Ped {
    const side = (this.rand() - 0.5) * 1.4;
    const pose = samplePath(path, s, side, [0, 0, 0, 0]);
    const p: Ped = { path, s, dir: this.rand() < 0.5 ? 1 : -1, speed: 1.05 + this.rand() * 0.6, side, look: this.rand(), phase: this.rand() * Math.PI * 2, pause: 0, dead: false, prev: [...pose] as Ped['prev'], cur: [...pose] as Ped['cur'] };
    if (p.dir < 0) p.cur[3] += Math.PI;
    p.prev = [...p.cur] as Ped['prev'];
    this.peds.push(p);
    return p;
  }

  /**
   * Advance everyone by `dt`. A walker with an obstacle (the player) just ahead stops, idles, and turns
   * back if still blocked after a few seconds. Obstacles moving faster than 3 m/s (the player's car)
   * are threats: people in their way run, along the path away from it and sideways out of its line.
   */
  step(dt: number, obstacles: readonly Obstacle[] = []): void {
    for (const p of this.peds) {
      if (p.dead) continue;
      p.prev = p.cur;
      for (const o of obstacles) if (o.vx !== undefined) this.flee(p, o);
      if (p.panic && p.panic > 0) {
        p.panic -= dt;
        p.pause = 0;
        this.move(p, dt, 2.4);
        continue;
      }
      if (obstacles.length && blockedAhead(p, obstacles)) {
        p.waited = (p.waited ?? 0) + dt;
        if (p.waited > 3) {
          p.dir = p.dir > 0 ? -1 : 1;
          p.waited = 0;
        } else {
          p.pause = Math.max(p.pause, 0.3);
        }
      } else if (p.pause <= 0) p.waited = 0;
      if (p.pause > 0) {
        p.pause -= dt;
        p.cur = [...p.prev] as Ped['cur'];
        continue;
      }
      if (this.rand() < dt * 0.02) p.pause = 2 + this.rand() * 5;
      if (this.rand() < dt * 0.01) p.dir = p.dir > 0 ? -1 : 1;
      this.move(p, dt, 1);
    }
    for (let i = this.peds.length - 1; i >= 0; i--) if (this.peds[i].dead) this.peds.splice(i, 1);
  }

  /** Start running if `o` (moving) is coming at `p`: within 14 m ahead of it and 3.5 m of its line. */
  private flee(p: Ped, o: Obstacle): void {
    const vx = o.vx ?? 0;
    const vz = o.vz ?? 0;
    const v = Math.hypot(vx, vz);
    if (v < 3) return;
    const ux = vx / v;
    const uz = vz / v;
    const rx = p.cur[0] - o.x;
    const rz = p.cur[2] - o.z;
    const along = rx * ux + rz * uz;
    const lat = rx * -uz + rz * ux;
    if (along < -2 || along > 14 || Math.abs(lat) > 3.5) return;
    if (!p.panic || p.panic <= 0) {
      // The path's +s direction t and its left normal n (samplePath offsets `side` along n), and the
      // way out of the car's line e.
      const h = p.cur[3] + (p.dir < 0 ? Math.PI : 0);
      const tx = Math.cos(h);
      const tz = -Math.sin(h);
      const nx = Math.sin(h);
      const nz = Math.cos(h);
      const ex = -uz * Math.sign(lat || 1);
      const ez = ux * Math.sign(lat || 1);
      // Run along the path whichever way leaves the line (or, if the path runs along it, away from the
      // car), and step sideways out of the line within the sidewalk.
      const off = tx * ex + tz * ez;
      const away = Math.abs(off) > 0.3 ? off : tx * rx + tz * rz;
      p.dir = away >= 0 ? 1 : -1;
      p.side = Math.max(-1.3, Math.min(1.3, p.side + Math.sign(nx * ex + nz * ez || 1) * 0.9));
    }
    p.panic = 3;
  }

  /** Walk `p` along its path at `boost` × its pace. */
  private move(p: Ped, dt: number, boost: number): void {
    {
      p.s += p.dir * p.speed * boost * dt;
      const L = p.path.length;
      if (p.path.closed) p.s = ((p.s % L) + L) % L;
      else if (p.s < 0 || p.s > L) {
        p.s = Math.max(0, Math.min(L, p.s));
        p.dir = p.dir > 0 ? -1 : 1;
      }
      p.phase += dt * p.speed * boost * 5.2;
      const pose = samplePath(p.path, p.s, p.side, [0, 0, 0, 0]);
      if (p.dir < 0) pose[3] += Math.PI;
      p.cur = pose;
    }
  }
}

/** Something people keep clear of: the player, their car; with a velocity (m/s) when moving. */
export interface Obstacle {
  x: number;
  z: number;
  vx?: number;
  vz?: number;
}

/** Is one of `obstacles` within 1.6 m ahead of `p` (and 0.9 m to either side)? */
function blockedAhead(p: Ped, obstacles: readonly { x: number; z: number }[]): boolean {
  const h = p.cur[3];
  const fx = Math.cos(h);
  const fz = -Math.sin(h);
  for (const o of obstacles) {
    const rx = o.x - p.cur[0];
    const rz = o.z - p.cur[2];
    const along = rx * fx + rz * fz;
    if (along < 0.2 || along > 1.6) continue;
    if (Math.abs(-rx * fz + rz * fx) < 0.9) return true;
  }
  return false;
}
